import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * Floating install button. Shows when the browser fires 'beforeinstallprompt'
 * (Android Chrome, Edge, Samsung Internet). On iOS Safari, shows manual guide.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    // Already installed?
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Dismissed recently?
    const dismissed = localStorage.getItem('fl_installDismissed');
    if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    const ua = window.navigator.userAgent || '';
    const ios = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
    setIsIOS(ios);
    if (ios) {
      setVisible(true);
      return;
    }

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', () => setVisible(false));
    return () => window.removeEventListener('beforeinstallprompt', onBip);
  }, []);

  const install = async () => {
    if (isIOS) { setShowIOSHint(true); return; }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem('fl_installDismissed', String(Date.now()));
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed bottom-4 right-4 z-40 max-w-[calc(100vw-2rem)] animate-fade-up">
        <div className="card-glow rounded-xl p-4 pr-3 flex items-center gap-3 shadow-2xl backdrop-blur">
          <div className="w-10 h-10 rounded-lg bg-[#d6af6c] flex items-center justify-center flex-shrink-0">
            <Download size={18} strokeWidth={2.2} className="text-[#1a140a]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#f2ecdf]">Instalar AXSELL</div>
            <div className="text-xs text-[#a89e87] truncate">Use como app no seu celular</div>
          </div>
          <button
            onClick={install}
            className="btn-gold px-3 py-2 rounded-md text-xs font-semibold flex-shrink-0"
          >
            Instalar
          </button>
          <button onClick={dismiss} className="text-[#7d735e] hover:text-[#e6dcc7] p-1" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
      </div>

      {showIOSHint && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setShowIOSHint(false)}>
          <div onClick={(e) => e.stopPropagation()} className="card-glow rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl text-[#f2ecdf]">Instalar no iPhone / iPad</h3>
              <button onClick={() => setShowIOSHint(false)} className="text-[#7d735e]"><X size={18} /></button>
            </div>
            <ol className="text-sm text-[#e6dcc7] space-y-3 list-decimal pl-5">
              <li>Toque no botão <b>Compartilhar</b> (ícone de quadrado com seta para cima) no rodapé do Safari.</li>
              <li>Role e toque em <b>Adicionar à Tela de Início</b>.</li>
              <li>Toque em <b>Adicionar</b> no canto superior direito.</li>
            </ol>
            <p className="text-xs text-[#7d735e] mt-4">O AXSELL vai aparecer como um app na sua tela inicial.</p>
          </div>
        </div>
      )}
    </>
  );
}
